import Box from '@mui/material/Box';
import { alpha } from '@mui/material/styles';
import TableCell from '@mui/material/TableCell';
import React, { useCallback } from 'react';

import { CELL_BUTTON_SX, getHeatColor, NUMERIC_CELL_SX } from './ServiceActivityMatrix.styles';
import { MatrixFocusCell } from '../../../@types/service-activity.type';
import { COLORS } from '../../../configs/colors';
import { ActivityStatusColumn } from '../../../constants/service-activity.constant';
import { getIntensityStep } from '../../../utils/service-activity-matrix';

export interface MatrixCellProps {
  column: ActivityStatusColumn;
  value: number;
  columnMax: number;
  /** Row description used in the cell's accessible name, e.g. `FSG Install`. */
  rowLabel: string;
  focus: MatrixFocusCell;
  isActive: boolean;
  canDrillDown: boolean;
  onSelect: (statusGroup: string) => void;
  onKeyDown: (event: React.KeyboardEvent, cell: MatrixFocusCell) => void;
  registerRef: (node: HTMLElement | null) => void;
}

/**
 * One cell of the matrix.
 *
 * Non-zero cells render a real `<button>` inside the `<td>` — the old table
 * put `role="button"` on the cell itself, which trips
 * `no-noninteractive-element-to-interactive-role` and loses native keyboard
 * behaviour. Zero cells render a muted `0` and are skipped by arrow
 * navigation: drilling into an empty job list is a dead end.
 */
export const MatrixCell: React.FC<MatrixCellProps> = ({
  column,
  value,
  columnMax,
  rowLabel,
  focus,
  isActive,
  canDrillDown,
  onSelect,
  onKeyDown,
  registerRef,
}) => {
  const handleClick = useCallback(() => onSelect(column.group), [onSelect, column.group]);
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => onKeyDown(event, focus),
    [onKeyDown, focus]
  );

  const step = getIntensityStep(value, columnMax);
  const background = getHeatColor(step, Boolean(column.alert));

  if (value === 0 || !canDrillDown) {
    return (
      <TableCell sx={{ ...NUMERIC_CELL_SX, bgcolor: background }}>
        <Box
          component="span"
          sx={{
            display: 'block',
            py: '6px',
            fontSize: '13px',
            fontWeight: value === 0 ? 400 : 600,
            color: value === 0 ? COLORS.ActivityZeroText : COLORS.ActivityHeatText,
          }}
        >
          {value.toLocaleString()}
        </Box>
      </TableCell>
    );
  }

  return (
    <TableCell sx={{ ...NUMERIC_CELL_SX, bgcolor: background }}>
      <Box
        component="button"
        type="button"
        ref={registerRef}
        tabIndex={isActive ? 0 : -1}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        aria-label={`${rowLabel}, ${column.header}: ${value.toLocaleString()} jobs. Open the filtered job list.`}
        sx={{
          ...CELL_BUTTON_SX,
          py: '6px',
          // Darken whatever heat tint the cell already carries, rather than
          // painting a fixed colour over it.
          '&:hover': { bgcolor: (theme) => alpha(theme.palette.common.black, 0.08) },
        }}
      >
        {value.toLocaleString()}
      </Box>
    </TableCell>
  );
};
